/*
  useExperiments — client-side A/B testing hook.
  Fetches `/experiments/assign?path=X` on mount and returns a `getSlot(key, fallback)`
  helper the landing pages use to render the assigned variant content per slot.

  The `fp_vid` cookie is set server-side on first call; subsequent calls return
  the same variant so returning visitors have a stable experience.
*/
import { useEffect, useState, useCallback } from "react";
import client from "./api";

/**
 * @param {string} path — path key the backend uses to look up running experiments (e.g. "/lp/small-fleet")
 * @returns {{ ready: boolean, getSlot: (experimentKey: string, fallback?: string) => string, assignments: Record<string, {variant_id, content, slot_key}> }}
 */
export function useExperiments(path) {
  const [ready, setReady] = useState(false);
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await client.get("/experiments/assign", { params: { path } });
        if (!alive) return;
        const byKey = {};
        for (const a of data?.assignments || []) {
          byKey[a.experiment_key] = a;
        }
        setAssignments(byKey);
      } catch {/* silent — always fall through to defaults */}
      finally {
        if (alive) setReady(true);
      }
    })();
    return () => { alive = false; };
  }, [path]);

  const getSlot = useCallback(
    (experimentKey, fallback = "") => {
      const a = assignments[experimentKey];
      return (a && a.content) ? a.content : fallback;
    },
    [assignments],
  );

  return { ready, getSlot, assignments };
}
